import type { ApolloQueryResult } from '@apollo/client';
import objectHash from 'object-hash';
import { Fragment, useContext, useRef } from 'react';

import { initializeApollo } from '@/graphql/client';
import type { ContentfulHeroProductListing } from '@/graphql/contentful/api';
import type {
  CollectionCardsQuery,
  CollectionCardsQueryVariables,
  LanguageCodeEnum,
} from '@/graphql/saleor/api';
import { CollectionCardsDocument } from '@/graphql/saleor/api';
import {
  type ContentfulData,
  generateSectionKey,
  parseSaleorType,
} from '@/integrations/contentful';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';

import {
  type AllTheWatchesSectionOpts,
  renderContentfulAllTheWatchesSection,
} from './cms';

export const usePreRenderCmsSections = (
  allTheWatchesCmsData: ContentfulData[],
) => {
  const draftMode = useContext(DraftModeContext);
  const cmsContentMap = useRef(new Map());
  const hashMap = useRef(new Map());

  const sectionsOpts: AllTheWatchesSectionOpts = [
    {
      type: 'Placeholder',
      opts: {
        type: 'product listing',
      },
    },
    {
      type: 'HeroProductListing',
      opts: {
        type: 'all the watches',
      },
    },
  ];

  const buildCms = (data: ContentfulData, i: number) => {
    let hash = hashMap.current.get(data);

    if (!hash) {
      hash = objectHash(data);
      hashMap.current.set(data, hash);
    }

    let el = cmsContentMap.current.get(hash);

    if (el) {
      return el;
    }

    el = (
      <Fragment key={generateSectionKey(data, i)}>
        {renderContentfulAllTheWatchesSection(
          data,
          sectionsOpts,
          undefined,
          draftMode,
        )}
      </Fragment>
    );

    cmsContentMap.current.set(hash, el);
    return el;
  };

  const cmsSectionAll = allTheWatchesCmsData.map(buildCms);

  return { cmsSectionAll };
};

const queryCollectionsData = async (
  ids: string[],
  channel: string,
  languageCode: LanguageCodeEnum,
): Promise<ApolloQueryResult<CollectionCardsQuery>> => {
  const client = initializeApollo();

  return client.query<CollectionCardsQuery, CollectionCardsQueryVariables>({
    query: CollectionCardsDocument,
    variables: {
      languageCode,
      filter: {
        ids,
      },
      channel,
    },
  });
};

export const fetchSelectedCollections = async (
  contentfulData: ContentfulData[],
  channel: string,
  languageCode: LanguageCodeEnum,
): Promise<string[]> => {
  if (!contentfulData) {
    return [];
  }

  const heroProductListing = contentfulData.find(
    (item) => item.__typename === 'HeroProductListing',
  ) as ContentfulHeroProductListing | undefined;

  let collectionIds: string[] | undefined;
  if (heroProductListing) {
    collectionIds = heroProductListing.collectionItemsCollection?.items.reduce<
      string[]
    >((ids, collectionItem) => {
      if (collectionItem) {
        const saleorCollection = parseSaleorType(
          collectionItem.saleorCollection,
        );

        if (saleorCollection?.ID) {
          ids.push(saleorCollection?.ID);
        }
      }

      return ids;
    }, []);
  }

  if (!collectionIds?.length) {
    return [];
  }

  const { data } = await queryCollectionsData(
    collectionIds,
    channel,
    languageCode,
  );

  if (!data.collections) {
    return [];
  }

  return data.collections.edges.map(({ node }) => node.name);
};
